import { TeamMember } from "../../../../@types/custom";
import { db } from "firebaseConfig";

/**
 * The team members' USER DOCUMENTS (name, photo, virtual numbers) — for the calls page's
 * member filter, which is the one caller (`useCallsData`).
 *
 * ⚠️ THIS IS NOT WHERE THE TEAM SCREEN'S CONTACT COUNT COMES FROM. The "contacts" column
 * of the admin's team table (`TeamMemberTable`) is `totalContactsCount` on the server's
 * `POST /get-data/org-admin-stats` answer: each member's book counted through the server's
 * read door. For a member of a pooled agency that is the office's records they HOLD.
 *
 * `shouldGetContacts` reads a different number — the aggregate a Cloud Function keeps on
 * every write of a contact DOCUMENT (`contacts-agg-count-total/total`). That number stops
 * moving the day an owner's book is cut over to Postgres (the contacts program's plan D3 /
 * D4: their documents are frozen, a pooled member's are gone), and nothing says so; it just
 * goes stale. So it is OFF unless a caller asks for it, no caller does, and it goes with
 * the function at P5. Never show it beside a name: the server's count is the count.
 * `teamContactCountSource.test.ts` pins all three.
 */
export async function getMyTeamData(teamMembers: string[], shouldGetContacts: boolean = false) {
  let result: TeamMember[] = [];

  if (teamMembers.length === 0) {
    return result;
  }

  const userDataPromises = [];
  const userContactsPromises = [];

  for (let teamMember of teamMembers) {
    userDataPromises.push(db.collection("dashboard-users-v2").doc(teamMember));

    // The Cloud-Function aggregate — frozen for a cut-over owner; see the header.
    if (shouldGetContacts) {
      userContactsPromises.push(
        db.collection("dashboard-users-v2").doc(teamMember).collection("contacts-agg-count-total")
      );
    }
  }

  try {
    const data = await Promise.all([
      ...userDataPromises.map((userData) => userData.get()),
      ...userContactsPromises.map((contactData) => contactData.get())
    ]);

    // first 1/2 of resolved promises data array should be users data
    const userData = data.slice(0, teamMembers.length).map((d) => d.data());

    // second 1/2 of resolved promises data array should be contacts count data
    const contactsCount = data.slice(teamMembers.length, teamMembers.length * 2);

    for (let i = 0; i < teamMembers.length; i++) {
      const { count: cc } = contactsCount[i]?.docs[0]?.data() ?? {};
      result.push({
        ...userData[i],
        email: teamMembers[i],
        contactsCount: cc ?? 0
      });
    }
  } catch (error) {
    console.error(error, "Error retrieving Team members data");
  }

  return result;
}
